import ApprovalIcon from "@mui/icons-material/Approval";
import EditIcon from "@mui/icons-material/Edit";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import React from "react";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Box,
  IconButton,
  ListItemButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { naturalSort } from "../../utils/naturalSort";

export default function SimpleList(props) {
  const theme = useTheme();
  const { palette } = theme;
  const { error = {}, primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenLight = isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.08)";
  const bgDefault = background?.default || (isDark ? "#021e2d" : "#f8fafc");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");

  const {
    disabled = false,
    isClassification = false,
    isColor,
    items,
    itemBtns,
    noSelectHighlight,
    onClick,
    onClickClone,
    onClickEdit,
    onClickRemove,
  } = props;

  const [selectedIndex, setSelectedIndex] = React.useState(null);

  // Empty state
  if (!items || items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          textAlign: "center",
          color: textSecondary,
        }}
      >
        <Typography variant="body2" sx={{ color: textSecondary }}>
          No items to display
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <List
        component="nav"
        aria-label="item list"
        sx={{
          padding: 0,
          "& .MuiListItem-root": {
            padding: 0,
            marginBottom: "8px",
          },
        }}
      >
        {items
          .filter((el) => el !== null)
          .sort((a, b) => naturalSort(a.label, b.label))
          .map((item, idx) => {
            const isSelected = selectedIndex === idx;
            
            return (
              <ListItem
                aria-selected={isSelected}
                key={`${idx} - ${item.id}`}
                disablePadding
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: isSelected ? `2px solid ${greenColor}` : "2px solid transparent",
                  backgroundColor: isSelected ? greenLight : "transparent",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: isSelected ? greenLight : (isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc"),
                  },
                }}
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginRight: "8px" }}>
                    {onClickEdit ? (
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          data-cypress-id={`${itemBtns?.edit}-${item.id
                            ? item.id.replace(/\s+/g, "-")
                            : item.label
                          }`}
                          onClick={() => {
                            onClickEdit(item.id);
                            setSelectedIndex(null);
                            if (onClick) {
                              onClick(isClassification ? item.name : item.id);
                            }
                          }}
                          size="small"
                          sx={{
                            color: textSecondary,
                            padding: "8px",
                            borderRadius: "8px",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(0, 40, 67, 0.1)",
                              color: isDark ? greenColor : navyColor,
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: "1.125rem" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    
                    {onClickClone ? (
                      <Tooltip title="Clone (use as template)" arrow>
                        <IconButton
                          aria-label="clone (use as template for new form)"
                          data-cypress-id={`${itemBtns?.clone}-${item.id
                            ? item.id.replace(/\s+/g, "-")
                            : item.label
                          }`}
                          onClick={(e) => {
                            onClickClone(item.id);
                            setSelectedIndex(null);
                            e.stopPropagation();
                          }}
                          size="small"
                          sx={{
                            color: textSecondary,
                            padding: "8px",
                            borderRadius: "8px",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)",
                              color: greenColor,
                            },
                          }}
                        >
                          <ApprovalIcon sx={{ fontSize: "1.125rem" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    
                    {onClickRemove ? (
                      <Tooltip title="Remove" arrow>
                        <IconButton
                          data-cypress-id={`${itemBtns?.remove}-${item.id
                            ? item.id.replace(/\s+/g, "-")
                            : item.label
                          }`}
                          onClick={(e) => {
                            onClickRemove(item.id ? item.id : item.customId);
                            setSelectedIndex(null);
                            e.stopPropagation();
                          }}
                          size="small"
                          sx={{
                            color: textSecondary,
                            padding: "8px",
                            borderRadius: "8px",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: isDark ? "rgba(248, 113, 113, 0.15)" : "rgba(239, 68, 68, 0.1)",
                              color: isDark ? "#f87171" : (error?.main || "#ef4444"),
                            },
                          }}
                        >
                          <RemoveCircleOutlineIcon sx={{ fontSize: "1.125rem" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                }
              >
                <ListItemButton
                  disabled={disabled}
                  selected={!noSelectHighlight ? isSelected : false}
                  onClick={() => {
                    if (onClick) {
                      onClick(isClassification ? item.name : item.id);
                    }
                    setSelectedIndex(idx);
                  }}
                  sx={{
                    padding: "12px 16px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease-in-out",
                    "&.Mui-selected": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {/* Color indicator dot */}
                  {isColor && item.color && (
                    <Box
                      sx={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        marginRight: "12px",
                        flexShrink: 0,
                        border: "2px solid rgba(255,255,255,0.8)",
                        boxShadow: `0 0 0 1px ${item.color}`,
                      }}
                    />
                  )}
                  <ListItemText
                    primary={item.label || item.name}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "0.9375rem",
                        color: isColor && item.color ? item.color : (isSelected ? (isDark ? greenColor : navyColor) : textPrimary),
                        fontFamily: '"Lato", sans-serif',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
    </Box>
  );
}
